import { useEffect } from 'react';
import { useStore } from '@betgames/bg-state-manager';
import { GameId } from '@bg-shared/enums';
import { partnerSettingsStore } from '@bg-shared/business/PartnerSettings';
import { authStore } from '@bg-shared/business/Auth';
import { chatServiceSupabase } from '../Chat.service';
import { chatStoreSupabase } from '../Chat.store';
import { ChatUtilities } from '../Chat.utilities';
import { supabaseService } from '../Supabase.provider';

export const useChat = (gameId: GameId) => {
    const { isInitialized } = useStore(chatStoreSupabase);
    const { isLoggedIn, selfProfile } = useStore(chatStoreSupabase);
    const isAuthenticated = useStore(authStore);

    const isChatEnabled = partnerSettingsStore.isChatEnabled(gameId);

    useEffect(() => {
        if (isAuthenticated && !isLoggedIn && !isInitialized && isChatEnabled) {
            supabaseService.setup();
        }
    }, []);

    useEffect(() => {
        if (!isLoggedIn && isInitialized && isChatEnabled) {
            chatServiceSupabase.login();
        }
    }, [isInitialized]);

    useEffect(() => {
        const unsubscribers: (() => void)[] = [];

        const initChat = async () => {
            chatStoreSupabase.setIsLoading(true);

            try {
                const roomData = await chatServiceSupabase.fetchRoomByGameId(gameId);

                if (!roomData) {
                    ChatUtilities.handleError('no_room_error');
                    return;
                }

                await chatServiceSupabase.fetchMessages(roomData.id);

                Promise.all([
                    chatServiceSupabase.subscribeToMessages(roomData.id),
                    chatServiceSupabase.subscribeToRoom(roomData.id),
                    chatServiceSupabase.subscribeToProfile(selfProfile.id),
                ]).then(([unsubscribeMessages, unsubscribeRoom, unsubscribeProfile]) => {
                    unsubscribers.push(unsubscribeMessages, unsubscribeRoom, unsubscribeProfile);
                    chatStoreSupabase.setIsReady(true);
                });
            } catch (error) {
                ChatUtilities.handleError(error);
            } finally {
                chatStoreSupabase.setIsLoading(false);
            }
        };

        if (isLoggedIn && isChatEnabled) {
            initChat();
        }

        return () => {
            unsubscribers.forEach((unsubscribe) => unsubscribe());
        };
    }, [gameId, isLoggedIn, isChatEnabled, isAuthenticated]);
};
